import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import apiClient from '../api/client';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Briefcase,
  CheckSquare,
  DollarSign,
  Mail,
  User,
  ArrowLeft,
  Calendar,
  Clock,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useAuth } from '../contexts/AuthContext';
import { getCurrencySymbol } from '../utils/currency';

const TeamMemberDetails = () => {
  const { user } = useAuth();
  const { memberId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // payment form state
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    paid_date: '',
    notes: '',
    payment_method: 'cash',
    currency: user?.currency || 'INR'
  });
  const [creatingPayment, setCreatingPayment] = useState(false);

  useEffect(() => {
    if (user?.currency) {
      setPaymentForm(prev => ({ ...prev, currency: user.currency }));
    }
  }, [user?.currency]);

  const handleCreatePayment = async () => {
    if (!paymentForm.amount) {
      toast.error('Amount is required');
      return;
    }
    try {
      setCreatingPayment(true);
      await apiClient.post(`/team/${memberId}/payments`, {
        amount: Number(paymentForm.amount),
        paid_date: paymentForm.paid_date || new Date().toISOString(),
        notes: paymentForm.notes,
        payment_method: paymentForm.payment_method,
        currency: paymentForm.currency
      });
      toast.success('Payment recorded');
      setPaymentForm({ ...paymentForm, amount: '', notes: '' });
      fetchDetails();
    } catch (err) {
      toast.error('Failed to record payment');
    } finally {
      setCreatingPayment(false);
    }
  }

  const fetchDetails = React.useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/team/${memberId}?analytics=true`);
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load member details');
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  useEffect(() => {
    if (data && !loading) {
      const hasSeenTour = localStorage.getItem('hasSeenTeamMemberTour');
      if (!hasSeenTour) {
        const driverObj = driver({
          showProgress: true,
          steps: [
            {
              element: '[data-testid="member-stats"]',
              popover: {
                title: 'Performance Stats',
                description: 'Track task completion rates and total earnings for this team member.',
                side: "bottom",
                align: 'start'
              }
            },
            {
              element: '[data-testid="member-summary"]',
              popover: {
                title: 'Skills & Summary',
                description: 'View a quick summary of active projects and specific skills.',
                side: "right",
                align: 'start'
              }
            },
            {
              element: '[data-testid="record-payment-card"]',
              popover: {
                title: 'Payments',
                description: 'Record and track payments made to this team member.',
                side: "right",
                align: 'start'
              }
            }
          ],
          onDestroyed: () => {
            localStorage.setItem('hasSeenTeamMemberTour', 'true');
          }
        });
        setTimeout(() => driverObj.drive(), 1000);
      }
    }
  }, [data, loading]);


  if (loading) return <div className="flex items-center justify-center h-48">Loading...</div>;
  if (!data) return <div className="p-6">Member not found</div>;

  const { member, projects, payments, tasks, analytics } = data;

  return (
    <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div className="space-y-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.history.back()}
            className="p-0 hover:bg-transparent text-slate-500 hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Team
          </Button>
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-2xl bg-indigo-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg shadow-indigo-100">
              {member?.name?.charAt(0) || '?'}
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">{member?.name || 'Unknown Member'}</h1>
              <div className="flex items-center gap-3 mt-1">
                <Badge variant="secondary" className="bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border-none px-3">
                  {member?.role || 'No Role'}
                </Badge>
                <span className="text-slate-400 text-sm flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  {member?.email || 'No Email'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="member-stats">
          <Card className="border-slate-200 shadow-sm bg-white">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-500 uppercase">Tasks Completed</CardTitle>
              <CheckSquare className="h-5 w-5 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900">{analytics.completed_tasks} / {analytics.total_tasks}</div>
              <p className="text-xs text-slate-500 mt-1">{analytics.task_completion_rate}% completion rate</p>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm bg-white">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-500 uppercase">Projects</CardTitle>
              <Briefcase className="h-5 w-5 text-indigo-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900">{analytics.total_projects}</div>
              <p className="text-xs text-slate-500 mt-1">Assigned projects</p>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm bg-white">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-500 uppercase">Total Paid</CardTitle>
              <DollarSign className="h-5 w-5 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-emerald-600">{getCurrencySymbol(user?.currency)}{analytics.total_earned.toLocaleString()}</div>
              <p className="text-xs text-slate-500 mt-1">Lifetime earnings</p>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm bg-white">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-500 uppercase">Efficiency</CardTitle>
              <Clock className="h-5 w-5 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900">{analytics.avg_tasks_per_project}</div>
              <p className="text-xs text-slate-500 mt-1">Avg tasks per project</p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Sidebar Info */}
        <div className="space-y-6">
          <Card className="border-slate-200 shadow-sm overflow-hidden" data-testid="member-summary">
            <CardHeader className="bg-slate-50/50 border-b border-slate-100">
              <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                <User className="h-4 w-4" />
                Member Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="flex justify-between items-center py-1">
                <span className="text-slate-500 text-sm">Active Projects</span>
                <span className="font-bold text-slate-900">{projects.length}</span>
              </div>
              <div className="flex justify-between items-center py-1">
                <span className="text-slate-500 text-sm">Total Tasks</span>
                <span className="font-bold text-slate-900">{tasks.length}</span>
              </div>
              <div className="flex justify-between items-center py-1">
                <span className="text-slate-500 text-sm">Total Paid</span>
                <span className="font-bold text-emerald-600">{getCurrencySymbol(user?.currency)}{(payments || []).reduce((s, p) => s + (p.amount || 0), 0).toLocaleString()}</span>
              </div>
              <div className="pt-4 border-t border-slate-100">
                <p className="text-xs font-bold text-slate-400 uppercase mb-2">Skills</p>
                <div className="flex flex-wrap gap-2">
                  {(member.skills || []).map(skill => (
                    <Badge key={skill} variant="outline" className="bg-white text-slate-600 border-slate-200">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm" data-testid="record-payment-card">
            <CardHeader className="bg-slate-50/50 border-b border-slate-100">
              <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Record Payment
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-400 uppercase">Amount ({getCurrencySymbol(user?.currency)})</label>
                  <input
                    className="w-full border border-slate-200 px-3 py-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none"
                    placeholder="0.00"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-400 uppercase">Date</label>
                  <input
                    type="date"
                    className="w-full border border-slate-200 px-3 py-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none"
                    value={paymentForm.paid_date}
                    onChange={(e) => setPaymentForm({ ...paymentForm, paid_date: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-400 uppercase">Method</label>
                    <select
                      className="w-full border border-slate-200 px-3 py-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none bg-white text-sm"
                      value={paymentForm.payment_method}
                      onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                    >
                      <option value="cash">Cash</option>
                      <option value="bank">Bank Transfer</option>
                      <option value="upi">UPI</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-400 uppercase">Currency</label>
                    <select
                      className="w-full border border-slate-200 px-3 py-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none bg-white text-sm"
                      value={paymentForm.currency}
                      onChange={(e) => setPaymentForm({ ...paymentForm, currency: e.target.value })}
                    >
                      <option value="INR">INR</option>
                      <option value="USD">USD</option>
                      <option value="GBP">GBP</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-400 uppercase">Notes</label>
                  <textarea
                    className="w-full border border-slate-200 px-3 py-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none resize-none"
                    placeholder="What is this payment for?"
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                    rows={2}
                  />
                </div>
                <Button
                  onClick={handleCreatePayment}
                  disabled={creatingPayment}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl py-6 font-bold"
                >
                  {creatingPayment ? 'Recording...' : 'Confirm Payment'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-2 space-y-8">
          {/* Projects Section */}
          <section className="space-y-4">
            <div className="flex items-center gap-2 px-1">
              <Briefcase className="h-5 w-5 text-indigo-600" />
              <h2 className="text-xl font-bold text-slate-900">Active Projects</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {projects.length === 0 ? (
                <div className="col-span-2 py-12 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200 text-slate-400">
                  Not currently assigned to any projects.
                </div>
              ) : (
                projects.map(p => (
                  <Link key={p.project_id} to={`/projects/${p.project_id}`} className="group">
                    <Card className="border-slate-200 shadow-sm group-hover:border-indigo-300 group-hover:shadow-md transition-all h-full">
                      <CardContent className="p-5 flex flex-col justify-between h-full">
                        <div>
                          <div className="flex justify-between items-start mb-2">
                            <h3 className="font-bold text-slate-900 group-hover:text-indigo-600 transition-colors line-clamp-1">{p.title}</h3>
                            <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-indigo-400 transition-all group-hover:translate-x-1" />
                          </div>
                          <Badge variant="secondary" className="text-[10px] font-bold uppercase tracking-wider mb-4">
                            {p.status}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between pt-4 border-t border-slate-50">
                          <span className="text-xs text-slate-500">Budget</span>
                          <span className="font-bold text-slate-900 text-sm">{getCurrencySymbol(user?.currency)}{p.total_budget.toLocaleString()}</span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))
              )}
            </div>
          </section>

          {/* Tasks Section */}
          <section className="space-y-4">
            <div className="flex items-center gap-2 px-1">
              <CheckSquare className="h-5 w-5 text-indigo-600" />
              <h2 className="text-xl font-bold text-slate-900">Assigned Tasks</h2>
            </div>
            <Card className="border-slate-200 shadow-sm overflow-hidden">
              <CardContent className="p-0">
                {tasks.length === 0 ? (
                  <div className="py-12 text-center text-slate-400 italic">
                    No tasks currently assigned.
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {tasks.map(t => (
                      <div key={t.task_id} className="p-4 hover:bg-slate-50 transition-colors flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-slate-900 truncate">{t.title}</h4>
                          <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {t.column_id.toUpperCase()}
                            </span>
                            <span>•</span>
                            <span className={`capitalize font-medium ${
                              t.priority === 'high' ? 'text-rose-500' :
                              t.priority === 'medium' ? 'text-amber-500' : 'text-emerald-500'
                            }`}>
                              {t.priority} Priority
                            </span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-[10px] font-bold text-slate-400 uppercase">Assigned</p>
                          <p className="text-xs text-slate-600 font-medium">{new Date(t.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </section>

          {/* Payment History Section */}
          <section className="space-y-4">
            <div className="flex items-center gap-2 px-1">
              <Clock className="h-5 w-5 text-indigo-600" />
              <h2 className="text-xl font-bold text-slate-900">Payment History</h2>
            </div>
            <Card className="border-slate-200 shadow-sm overflow-hidden">
              <CardContent className="p-0">
                {payments.length === 0 ? (
                  <div className="py-12 text-center text-slate-400 italic text-sm">
                    No payment records found.
                  </div>
                ) : (
                  <table className="w-full text-left">
                    <thead className="bg-slate-50 border-b border-slate-100">
                      <tr>
                        <th className="px-6 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Amount</th>
                        <th className="px-6 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Method</th>
                        <th className="px-6 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Date</th>
                        <th className="px-6 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-sm">
                      {payments.map(p => (
                        <tr key={p.payment_id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4 font-bold text-emerald-600">{getCurrencySymbol(p.currency || user?.currency)}{p.amount.toLocaleString()}</td>
                          <td className="px-6 py-4 text-slate-600 capitalize">{p.payment_method || 'N/A'}</td>
                          <td className="px-6 py-4 text-slate-500">{p.paid_date ? new Date(p.paid_date).toLocaleDateString() : new Date(p.created_at).toLocaleDateString()}</td>
                          <td className="px-6 py-4 text-slate-500 truncate max-w-[200px]">{p.notes || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TeamMemberDetails;
